import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calculator, ShieldCheck, FileText, ArrowUpRight, Building2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";

const META = {
  rc: { label: "Preventivi RC", icon: Calculator, color: "#10B981", newLink: "/quotatore-rc/preventivi/nuovo" },
  cau: { label: "Pratiche Cauzioni", icon: ShieldCheck, color: "#F97316", newLink: "/cauzioni-pro" },
  mup: { label: "Pratiche MUP", icon: FileText, color: "#1B2380", newLink: "/mup/nuova" },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtEuro = (n) => (n == null ? "" : `€ ${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`);

function ModuleColumn({ module, items, nav }) {
  const m = META[module];
  const Icon = m.icon;
  return (
    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`cliente-col-${module}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between" style={{ backgroundColor: `${m.color}0D` }}>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: m.color }} />
          <span className="text-sm font-medium text-slate-800">{m.label}</span>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
      </div>
      <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {items.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-400">Nessun elemento</div>}
        {items.map((it) => (
          <button key={it.id} onClick={() => nav(it.link)} data-testid={`cliente-item-${module}-${it.id}`}
            className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800 truncate">{it.subtitle || "—"}</div>
              <div className="text-xs text-slate-400">{it.status_label} · {fmtDate(it.date)}</div>
            </div>
            {it.amount != null && <div className="text-xs text-slate-600 tabular-nums">{fmtEuro(it.amount)}</div>}
            <ArrowUpRight className="h-4 w-4 text-slate-300" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ClienteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/hub/cliente/${id}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [id]);

  const a = data?.anagrafica;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="cliente-detail-page">
        <button onClick={() => nav("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="cliente-back">
          <ArrowLeft className="h-4 w-4" /> Home
        </button>

        {!data ? (
          <div className="text-slate-400">Caricamento…</div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-8">
              <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <Building2 className="h-6 w-6 text-slate-500" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-medium text-slate-900">{a?.ragione_sociale || "Cliente"}</h1>
                <p className="text-slate-500 text-sm mt-1">
                  {a?.tipologia_ente || "—"}
                  {a?.partita_iva ? ` · P.IVA ${a.partita_iva}` : (a?.codice_fiscale ? ` · CF ${a.codice_fiscale}` : "")}
                  {a?.comune ? ` · ${a.comune}${a.provincia ? ` (${a.provincia})` : ""}` : ""}
                </p>
                <p className="text-slate-400 text-xs mt-1">{a?.email || ""}{a?.telefono ? ` · ${a.telefono}` : ""}</p>
              </div>
              <div className="ml-auto text-right">
                <div className="text-3xl font-heading font-semibold text-slate-900">{data.totale}</div>
                <div className="text-xs text-slate-500">elementi totali</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <ModuleColumn module="rc" items={data.rc} nav={nav} />
              <ModuleColumn module="cau" items={data.cau} nav={nav} />
              <ModuleColumn module="mup" items={data.mup} nav={nav} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
